import { useState, useRef, useEffect } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { Event } from '@/lib/calendar/types'
import { DraggableEventCard } from './DraggableEventCard'

interface DroppableDayCellProps {
  date: Date
  isToday: boolean
  isSelected: boolean
  events: Event[]
  onSelectDate: (date: Date) => void
  onMoveEvent: (eventId: string, newDate: Date) => void
  onEventClick: (event: Event) => void
  onViewPost?: (event: Event) => void
}

export function DroppableDayCell({
  date,
  isToday,
  isSelected: _isSelected,
  events,
  onSelectDate,
  onMoveEvent: _onMoveEvent,
  onEventClick,
  onViewPost,
}: DroppableDayCellProps) {
  const [_isHovered, setIsHovered] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showExpandButton, setShowExpandButton] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const cellRef = useRef<HTMLDivElement | null>(null)
  const MAX_HEIGHT = 65 // Approximate max height before overflow

  const { setNodeRef, isOver } = useDroppable({
    id: `day-${date.toISOString()}`,
    data: { date },
  })

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight
      const containerHeight = contentRef.current.clientHeight
      setShowExpandButton(contentHeight > containerHeight)
    }
  }, [events])

  // Scroll to cell when expanded
  useEffect(() => {
    if (isExpanded && cellRef.current) {
      setTimeout(() => {
        const rect = cellRef.current!.getBoundingClientRect()
        const offset = 100 // Additional scroll offset in pixels
        window.scrollBy({
          top: rect.top - window.innerHeight / 2 + offset,
          behavior: 'smooth',
        })
      }, 50)
    }
  }, [isExpanded])

  const visibleEvents = isExpanded ? events : events
  const hiddenCount =
    showExpandButton && !isExpanded ? events.length - Math.floor(MAX_HEIGHT / 35) : 0

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        cellRef.current = node
      }}
      className={`group relative flex min-h-[90px] cursor-pointer flex-col rounded-lg p-2 transition-all ${
        isToday ? 'bg-card hover:brightness-110' : 'bg-card hover:brightness-110'
      } ${isOver ? 'ring-primary ring-2 brightness-110' : ''}`}
      onClick={() => onSelectDate(date)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setIsExpanded(false)
      }}>
      <div className="mb-1 flex items-center justify-between">
        <div
          className={`text-left text-sm ${
            isToday
              ? 'bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-medium'
              : 'text-foreground'
          }`}>
          {date.getDate()}
        </div>
      </div>

      <div
        ref={contentRef}
        className={`flex-1 space-y-1 ${
          isExpanded ? 'overflow-y-auto' : 'overflow-hidden'
        } [scrollbar-color:rgba(255,255,255,0.2)_transparent] [scrollbar-width:thin]`}
        style={{ maxHeight: isExpanded ? '300px' : `${MAX_HEIGHT}px` }}>
        {visibleEvents.slice(0, isExpanded ? undefined : 2).map((event) => (
          <DraggableEventCard
            key={event.id}
            event={event}
            onEventClick={onEventClick}
            onViewPost={onViewPost}
          />
        ))}
      </div>

      {showExpandButton && hiddenCount > 0 && !isExpanded && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(true)
          }}
          className="mt-1 text-left text-xs text-white hover:text-white/80">
          +{hiddenCount} more
        </button>
      )}
    </div>
  )
}
